import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faCalculator, faFileAlt, faDownload, faHeart } from '@fortawesome/free-solid-svg-icons';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import TransparencyChart from '../components/donation/TransparencyChart';
import PartnershipForm from '../components/donation/PartnershipForm';
import { CardFrame, CardGrid, CardContent } from '../components/ui/card';
import api from '../api/api';
import { formatCurrency } from '../utils/formatCurrency';
import { useScrollReveal } from '../utils/hooks/useScrollReveal';
import './DonationPage.css';

const DonationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_donors: 0, supported_hospitals: 0, total_amount: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const guideRef = useScrollReveal();
  const donateFormRef = useScrollReveal();
  const statsRef = useScrollReveal();
  const transparencyRef = useScrollReveal();
  const partnershipRef = useScrollReveal();

  useEffect(() => {
    api.getStats().then(setStats).catch(console.error);
    api.getCampaigns().then(setCampaigns).catch(console.error);
  }, []);

  const handleDonateClick = () => {
    if (selectedCampaign) {
      navigate(`/donate/${selectedCampaign}`);
    } else {
      navigate('/donate');
    }
  };

  return (
    <>
      <Header />
      <main>
        {/* 기부 안내 */}
        <section ref={guideRef} className="donation-guide scroll-reveal">
          <div className="container">
            <h2 className="section-title">{t('donation.guide.title')}</h2>
            <CardGrid min="250px" gap="2rem" className="guide-grid">
              <CardFrame
                padding="2rem"
                radius="10px"
                shadow="normal"
                hover="md"
              >
                <h3>{t('donation.guide.regular')}</h3>
                <p>{t('donation.guide.regularDesc')}</p>
              </CardFrame>
              <CardFrame
                padding="2rem"
                radius="10px"
                shadow="normal"
                hover="md"
              >
                <h3>{t('donation.guide.onetime')}</h3>
                <p>{t('donation.guide.onetimeDesc')}</p>
              </CardFrame>
              <CardFrame
                padding="2rem"
                radius="10px"
                shadow="normal"
                hover="md"
              >
                <h3>{t('donation.guide.goods')}</h3>
                <p>{t('donation.guide.goodsDesc')}</p>
              </CardFrame>
            </CardGrid>
            <div className="donation-process">
              <h3>{t('donation.guide.process')}</h3>
              <ol>
                <li>{t('donation.guide.step1')}</li>
                <li>{t('donation.guide.step2')}</li>
                <li>{t('donation.guide.step3')}</li>
              </ol>
            </div>
          </div>
        </section>

        {/* 기부 신청하기 */}
        <section ref={donateFormRef} className="donate-form-section scroll-reveal">
          <div className="container">
            <div className="donate-form-card">
              <FontAwesomeIcon icon={faHeart} className="donate-icon" />
              <h2>지금 기부하기</h2>
              <p>생명을 구하는 여정에 동참해주세요</p>

              <div className="campaign-selector">
                <label htmlFor="campaign-select">캠페인 선택 (선택사항)</label>
                <select
                  id="campaign-select"
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  className="campaign-dropdown"
                >
                  <option value="">일반 후원 (자유롭게 사용)</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.title}
                    </option>
                  ))}
                </select>
              </div>

              <button onClick={handleDonateClick} className="donate-now-btn">
                <FontAwesomeIcon icon={faHeart} /> 기부하기
              </button>

              <p className="donate-note">
                선택한 캠페인이 없으면 가장 필요한 곳에 사용됩니다
              </p>
            </div>
          </div>
        </section>

        {/* 현재 기부 현황 */}
        <section ref={statsRef} className="donation-stats scroll-reveal">
          <div className="container">
            <h2 className="section-title">{t('donation.stats.title')}</h2>
            <div className="stats-highlight">
              <div className="stat-box">
                <div className="stat-number">{stats.total_donors}</div>
                <div className="stat-label">{t('donation.stats.donors')}</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{formatCurrency(stats.total_amount, i18n.language)}</div>
                <div className="stat-label">{t('donation.stats.totalAmount')}</div>
              </div>
            </div>
            <p className="stats-usage">{t('donation.stats.usageExample')}</p>
          </div>
        </section>

        {/* 투명성 강조 */}
        <section ref={transparencyRef} className="transparency-section scroll-reveal">
          <div className="container">
            <h2 className="section-title">{t('donation.transparency.title')}</h2>
            <p className="section-subtitle">{t('donation.transparency.subtitle')}</p>

            <div className="transparency-content">
              <TransparencyChart />

              <div className="transparency-actions">
                <CardGrid min="250px" gap="2rem">
                  <CardFrame
                    padding="2rem"
                    radius="15px"
                    shadow="normal"
                    hover="md"
                  >
                    <CardContent
                      variant="value"
                      icon={<FontAwesomeIcon icon={faEye} />}
                      title={<h4>{t('donation.transparency.realTime')}</h4>}
                      desc={<p>{t('donation.transparency.realTimeDesc')}</p>}
                      ctaLabel={t('donation.transparency.viewAccount')}
                      onCtaClick={() => navigate('/account-statement')}
                    />
                  </CardFrame>

                  <CardFrame
                    padding="2rem"
                    radius="15px"
                    shadow="normal"
                    hover="md"
                  >
                    <CardContent
                      variant="value"
                      icon={<FontAwesomeIcon icon={faFileAlt} />}
                      title={<h4>{t('donation.transparency.reports')}</h4>}
                      desc={<p>{t('donation.transparency.reportsDesc')}</p>}
                      ctaLabel={
                        <>
                          <FontAwesomeIcon icon={faDownload} /> {t('donation.transparency.downloadReport')}
                        </>
                      }
                      onCtaClick={() => {
                        // TODO: 백엔드에서 PDF 생성 API 필요
                      }}
                    />
                  </CardFrame>

                  <CardFrame
                    padding="2rem"
                    radius="15px"
                    shadow="normal"
                    hover="md"
                  >
                    <CardContent
                      variant="value"
                      icon={<FontAwesomeIcon icon={faCalculator} />}
                      title={<h4>{t('donation.transparency.zeroCost')}</h4>}
                      desc={<p>{t('donation.transparency.zeroCostDesc')}</p>}
                    />
                  </CardFrame>
                </CardGrid>
              </div>
            </div>
          </div>
        </section>

        {/* 파트너십 협력 제안 */}
        <section ref={partnershipRef} className="partnership-section scroll-reveal">
          <div className="container">
            <PartnershipForm />

            <div className="proposal-download">
              <h4>{t('donation.partnership.proposalTitle')}</h4>
              <p>{t('donation.partnership.proposalDesc')}</p>
              <button className="download-btn">
                <FontAwesomeIcon icon={faDownload} /> {t('donation.partnership.downloadProposal')}
              </button>
              {/* TODO: 후원제안서 PDF 파일 준비 필요 */}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default DonationPage;
